import React, { useState } from 'react';
import { InfoTooltip } from '../common/InfoTooltip';
import {
  useAdminExecutiveSummary,
  useAdminGenerateSummary,
  isInsufficientDataError,
  getInsufficientDataDetails,
  isCachedResponse,
} from '../../hooks/useAdminExecutiveSummary';

interface AdminExecutiveSummaryProps {
  userId: string;
  userLabel: string;
}

export const AdminExecutiveSummary: React.FC<AdminExecutiveSummaryProps> = ({ userId, userLabel }) => {
  const [showToast, setShowToast] = useState<{ type: 'success' | 'info' | 'error'; message: string } | null>(null);

  const { data, isLoading, error, refetch } = useAdminExecutiveSummary(userId);
  const generateMutation = useAdminGenerateSummary(userId);

  const handleGenerateSummary = async (force: boolean = false) => {
    try {
      const result = await generateMutation.mutateAsync({ force });
      if (isCachedResponse(result)) {
        setShowToast({
          type: 'info',
          message: 'No substantial changes detected. Showing cached summary.',
        });
      } else {
        setShowToast({
          type: 'success',
          message: 'Summary generated successfully!',
        });
      }
      setTimeout(() => setShowToast(null), 5000);
    } catch (err: any) {
      const insufficientData = getInsufficientDataDetails(err);
      if (insufficientData) {
        setShowToast({
          type: 'error',
          message: `${insufficientData.message}`,
        });
      } else {
        setShowToast({
          type: 'error',
          message: 'Failed to generate summary. Please try again.',
        });
      }
      setTimeout(() => setShowToast(null), 7000);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="animate-pulse">
          <div className="flex items-start mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-lg mr-4"></div>
            <div className="flex-1">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const noSummaryError = error && (error as any)?.response?.status === 404;
  const insufficientDataError = isInsufficientDataError(error);
  const insufficientDataDetails = getInsufficientDataDetails(error);

  if (noSummaryError || insufficientDataError) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0 p-2 bg-indigo-50 rounded-lg mr-4">
            <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Executive Summary</h3>
                <p className="text-sm text-gray-500">
                  AI-powered insights from {userLabel ? `${userLabel}'s` : 'this user’s'} communication patterns
                </p>
              </div>
              <InfoTooltip
                title="AI Executive Summary"
                content={[
                  'This section provides an AI-generated executive summary of the user’s communication style.',
                  'The summary includes overall style, key strengths, and growth opportunities.',
                  insufficientDataDetails
                    ? `They need at least ${insufficientDataDetails.required_count} analyses to generate a summary.`
                    : 'Ask the user to analyse more communications to unlock this feature.',
                ]}
                position="left"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          {insufficientDataDetails ? (
            <>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">More Data Needed</h4>
              <p className="text-gray-600 mb-4">
                They have {insufficientDataDetails.current_count} of {insufficientDataDetails.required_count} analyses
                needed.
              </p>
            </>
          ) : (
            <>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">No Summary Yet</h4>
              <p className="text-gray-600 mb-6">
                Generate an AI executive summary to see coaching insights for this user.
              </p>
            </>
          )}

          <button
            onClick={() => handleGenerateSummary(false)}
            disabled={generateMutation.isPending || insufficientDataDetails !== null}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              insufficientDataDetails
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {generateMutation.isPending ? 'Generating Summary...' : 'Generate Summary'}
          </button>
        </div>
      </div>
    );
  }

  if (error && !noSummaryError && !insufficientDataError) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0 p-2 bg-red-50 rounded-lg mr-4">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Summary</h3>
            <p className="text-sm text-gray-600 mb-4">
              {(error as any)?.response?.data?.message || (error as any)?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (data && data.summary && data.metadata) {
    const { summary, metadata } = data;
    const isGenerating = generateMutation.isPending;

    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        {showToast && (
          <div
            className={`mb-4 px-4 py-3 rounded-lg border ${
              showToast.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : showToast.type === 'info'
                ? 'bg-blue-50 border-blue-200 text-blue-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{showToast.message}</p>
              <button onClick={() => setShowToast(null)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className={`transition-opacity duration-300 ${isGenerating ? 'opacity-60' : 'opacity-100'}`}>
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0 p-2 bg-indigo-50 rounded-lg mr-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Executive Summary</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {metadata?.generated_at && (
                      <span>
                        Generated{' '}
                        {new Date(metadata.generated_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                    {metadata?.total_analyses && <span>• Based on {metadata.total_analyses} analyses</span>}
                    {metadata?.is_stale && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-medium">
                        Stale
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <InfoTooltip
                    title="AI Executive Summary"
                    content={[
                      'This AI-generated summary analyzes the user’s communication patterns across all analyses.',
                      'It highlights their communication style, key strengths, and areas for development.',
                      'The summary updates automatically when substantial changes are detected.',
                    ]}
                    position="left"
                  />
                  <button
                    onClick={() => handleGenerateSummary(true)}
                    disabled={isGenerating}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Force regenerate summary"
                  >
                    {isGenerating ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        <span>Refresh</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Communication Style</h4>
              <p className="text-base text-gray-700 leading-relaxed">{summary.communication_style}</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Strengths</h4>
              <ul className="space-y-2">
                {summary.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start text-base text-gray-700">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Growth Opportunities</h4>
              <ul className="space-y-2">
                {summary.growth_opportunities.map((opportunity, index) => (
                  <li key={index} className="flex items-start text-base text-gray-700">
                    <svg
                      className="w-5 h-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <span>{opportunity}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-start text-indigo-600">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <span className="font-medium leading-relaxed">{summary.overall_assessment}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback if structure unexpected
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-start">
        <div className="flex-shrink-0 p-2 bg-amber-50 rounded-lg mr-4">
          <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Executive Summary</h3>
          <p className="text-sm text-gray-600 mb-4">
            Unable to load summary. Please try refreshing the page or generating a new summary.
          </p>
          <button
            onClick={() => handleGenerateSummary(true)}
            disabled={generateMutation.isPending}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate Summary'}
          </button>
        </div>
      </div>
    </div>
  );
};

